/**
 * Reklam harcaması ve performansı — SALT OKUMA sözleşmesi (v3.1 Dalga 2B, dilim S11 — D8; ADR-0016 § 8). Belge:
 * docs/spec/castinflow/ad-spend.md.
 *
 * - Reklam hesabı = sağlayıcı bağlantısının (S7) reklam hesabı varlığıdır: `meta_ads_account`, `google_ads_customer` ya da
 *   (yalnız üretim dışı) `simulator_account`. Çalışma alanı bağlantı kaydından gelir; istek gövdesinden alınmaz.
 * - Castinflow reklam hesabına YAZMAZ: bağdaştırıcılar yalnız okuma uçlarını çağırabilir (izin listesi + statik test).
 * - Günlük satırın tarihi HESABIN saat dilimindeki takvim günüdür (platform raporu da öyle verir). Para birimi hesabındır;
 *   farklı para birimleri kur kaynağı olmadan TOPLANMAZ.
 * - "Platform dönüşümü" reklam platformunun kendi saydığı dönüşümdür; Castinflow satışı değildir.
 */
import { z } from 'zod';
import { addDaysIso, daysBetweenIso, localDateIso } from './activities.js';
import { zonedInstant } from './automation-schedule.js';
// ---------------------------------------------------------------------------------------------------------------------
// Hesaplar ve kaynaklar
// ---------------------------------------------------------------------------------------------------------------------
/** Reklam hesabı taşıyan bağlantı türleri */
export const AD_ACCOUNT_PROVIDER_KINDS = [
    'meta_ads_account',
    'google_ads_customer',
    'simulator',
];
/** Reklam hesabı varlık türleri */
export const AD_ACCOUNT_ASSET_TYPES = [
    'meta_ads_account',
    'google_ads_customer',
    'simulator_account',
];
/** Harcama verisinin kaynağı (satırda saklanır) */
export const AD_SPEND_SOURCES = ['simulator', 'google_ads', 'meta_ads'];
export const AD_SPEND_SOURCE_BY_ASSET = {
    meta_ads_account: 'meta_ads',
    google_ads_customer: 'google_ads',
    simulator_account: 'simulator',
};
export function isAdAccountAssetType(t) {
    return AD_ACCOUNT_ASSET_TYPES.includes(t);
}
/**
 * Kampanya kaydı Castinflow'un TEK kanonik kampanya varlığıdır (`acquisition_campaigns`; v3.1 S9). Harcama eşitlemesi gördüğü
 * kampanyayı orada (platform + düzey `campaign` + dış kimlik) yoksa açar ve reklam hesabına bağlar.
 */
export const AD_PLATFORM_BY_SOURCE = {
    google_ads: 'google',
    meta_ads: 'meta',
    simulator: 'other',
};
/** Platformun bildirdiği kampanya durumu (okunur; bu dilimde saklanmaz) */
export const AD_CAMPAIGN_STATUSES = ['enabled', 'paused', 'removed', 'unknown'];
export const AD_CAMPAIGN_ID_RE = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/;
export const AD_CURRENCY_RE = /^[A-Z]{3}$/;
export const AD_DATE_RE = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
// ---------------------------------------------------------------------------------------------------------------------
// Eşitleme kuralları
// ---------------------------------------------------------------------------------------------------------------------
/**
 * - `refreshDays`: her eşitlemede hesabın bugünü dahil son N gün YENİDEN çekilir (platformların geç düzeltmeleri; upsert).
 *   Daha eski satıra dokunulmaz.
 * - `backfillDays`: ilk bağlanmada (ve uzun kopukluktan sonra) geriye dönük en çok N gün çekilir; aşan kısım "boşluk" olarak
 *   kaydedilir ve raporda veri eksik uyarısı verir.
 * - `dailyLocalMinute`: TAKVİM esaslı günlük eşitleme saati — hesabın IANA saat diliminde her gün 04:00 (gece yarısından
 *   sonra; dünün verisi tamamlanmış olarak çekilir). UTC karşılığı yaz saatinde değişir, yerel saat 04:00 kalır.
 * - Geri çekilme: hız sınırında sağlayıcının bildirdiği süre (yoksa 15 dk); geçici hatada art arda hata sayısına göre artan.
 */
export const AD_SPEND_SYNC = {
    refreshDays: 3,
    backfillDays: 30,
    dailyLocalMinute: 4 * 60,
    /** Son başarılı eşitleme bundan eskiyse veri "bayat" sayılır */
    staleAfterHours: 48,
    rateLimitBackoffMinutes: 15,
    rateLimitBackoffMaxMinutes: 24 * 60,
    failureBackoffMinutes: [5, 15, 60, 240, 720],
    /** Worker turunda çalışma alanı başına en çok eşitlenen hesap */
    perTenantPerTick: 20,
    /** Rapor aralığı üst sınırı (gün) */
    maxRangeDays: 92,
    /** Kayıtlı boşluk sayısı üst sınırı (en yenileri tutulur) */
    maxGaps: 20,
};
/** Geçici hata sonrası bekleme (art arda hata sayısı 1'den başlar) */
export function failureBackoffMinutes(consecutiveFailures) {
    const steps = AD_SPEND_SYNC.failureBackoffMinutes;
    const i = Math.min(Math.max(consecutiveFailures, 1), steps.length) - 1;
    return steps[i] ?? 720;
}
// ---------------------------------------------------------------------------------------------------------------------
// Takvim günü yardımcıları (hesap saat dilimi; yaz saati çalışma zamanının saat dilimi verisiyle)
// ---------------------------------------------------------------------------------------------------------------------
/** Anın saat dilimindeki gece yarısından bu yana geçen dakika (0–1439) */
export function minuteOfDayIn(timeZone, at) {
    const p = {};
    for (const x of new Intl.DateTimeFormat('en-US', {
        timeZone,
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
    }).formatToParts(at))
        if (x.type !== 'literal')
            p[x.type] = x.value;
    return Number(p['hour'] ?? 0) * 60 + Number(p['minute'] ?? 0);
}
/**
 * `now`dan sonraki ilk yerel `minuteOfDay` anı (hesap saat diliminde; TAKVİM esaslı günlük plan). Yerel saat → UTC çevirisi
 * otomasyon takviminin DST kurallı yardımcısıyla (`zonedInstant`) yapılır: elle ofset yok; UTC karşılığı yaz saatinde değişir,
 * yerel saat aynı kalır.
 */
export function nextZonedDaily(now, timeZone, minuteOfDay) {
    const hh = String(Math.floor(minuteOfDay / 60)).padStart(2, '0');
    const mm = String(minuteOfDay % 60).padStart(2, '0');
    const today = localDateIso(timeZone, now);
    const t = zonedInstant(timeZone, today, `${hh}:${mm}`);
    return t.getTime() > now.getTime()
        ? t
        : zonedInstant(timeZone, addDaysIso(today, 1), `${hh}:${mm}`);
}
const minDate = (a, b) => (a <= b ? a : b);
const maxDate = (a, b) => (a >= b ? a : b);
/**
 * Eşitleme penceresi (hesabın saat dilimindeki "bugün"e göre):
 *   ilk eşitleme        → [bugün − (geriye dönük − 1), bugün]
 *   sonraki eşitlemeler → [max(bugün − (geriye dönük − 1), min(bugün − (yenileme − 1), son eşitlenen gün + 1)), bugün]
 * Böylece her eşitleme son 3 günü yeniden çeker, kopukluktan sonra kaçırılan günleri TEK istekle (en çok 30 gün) tamamlar;
 * birikmiş eşitlemeler topluca çalıştırılmaz. Son 3 günden eski ve daha önce çekilmiş güne dokunulmaz.
 */
export function adSyncWindow(today, syncedThrough) {
    const backfillStart = addDaysIso(today, -(AD_SPEND_SYNC.backfillDays - 1));
    if (syncedThrough === null)
        return { from: backfillStart, to: today, backfill: true, gap: null };
    const refreshStart = addDaysIso(today, -(AD_SPEND_SYNC.refreshDays - 1));
    const next = addDaysIso(syncedThrough, 1);
    const from = maxDate(backfillStart, minDate(refreshStart, next));
    const gap = next < backfillStart ? { from: next, to: addDaysIso(backfillStart, -1) } : null;
    return { from, to: today, backfill: false, gap };
}
// ---------------------------------------------------------------------------------------------------------------------
// Para: mikro birim (para biriminin milyonda biri) — kayan nokta YOK
// ---------------------------------------------------------------------------------------------------------------------
const DECIMAL_RE = /^(\d{1,13})(?:\.(\d{1,6}))?$/;
/** Ondalık dizge ("12.34") → mikro (12_340_000n). Negatif, bilimsel gösterim ve 6'dan fazla ondalık reddedilir. */
export function microsFromDecimal(s) {
    const m = DECIMAL_RE.exec(s.trim());
    if (m === null)
        throw new Error('geçersiz tutar');
    const whole = BigInt(m[1] ?? '0');
    const frac = BigInt((m[2] ?? '').padEnd(6, '0'));
    return whole * 1000000n + frac;
}
/** Tam sayı dizgesi ("1234560000") → mikro; int64 sınırında */
export function microsFromIntString(s) {
    if (!/^\d{1,19}$/.test(s))
        throw new Error('geçersiz mikro tutar');
    const v = BigInt(s);
    if (v > 9223372036854775807n)
        throw new Error('tutar sınır dışı');
    return v;
}
/** Mikro → ondalık dizge (en çok 6 ondalık, sondaki sıfırlar atılır; en az 2 ondalık) */
export function decimalFromMicros(micros) {
    const v = typeof micros === 'string' ? BigInt(micros) : micros;
    const neg = v < 0n;
    const abs = neg ? -v : v;
    const whole = abs / 1000000n;
    let frac = (abs % 1000000n).toString().padStart(6, '0').replace(/0+$/, '');
    if (frac.length < 2)
        frac = frac.padEnd(2, '0');
    return `${neg ? '-' : ''}${whole.toString()}.${frac}`;
}
// ---------------------------------------------------------------------------------------------------------------------
// Maliyet ölçüleri (ROAS / CAC) — bu dilimde hesaplanmaz; yardımcı yalnız koşullar sağlanınca değer verir
// ---------------------------------------------------------------------------------------------------------------------
/** Maliyet ölçüsü hesaplanamadığında neden */
export const COST_METRIC_GAPS = [
    'harcama_yok',
    'harcama_eksik',
    'harcama_sifir',
    'para_birimi_farkli',
    'donem_farkli',
    'gelir_yok',
    'payda_sifir',
];
function spendGap(spend) {
    if (spend === null)
        return 'harcama_yok';
    if (!spend.complete)
        return 'harcama_eksik';
    if (BigInt(spend.spendMicros) <= 0n)
        return 'harcama_sifir';
    return null;
}
/**
 * ROAS = gelir / harcama. Yalnız harcama MEVCUT, tam ve sıfırdan büyükse VE gelirle AYNI para birimindeyse değer verir;
 * aksi hâlde neden (`eksik`). Kur dönüşümü yapılmaz.
 */
export function roasOf(revenue, spend) {
    const g = spendGap(spend);
    if (g !== null || spend === null)
        return { ok: false, eksik: g ?? 'harcama_yok' };
    if (revenue === null)
        return { ok: false, eksik: 'gelir_yok' };
    if (revenue.currency !== spend.currency)
        return { ok: false, eksik: 'para_birimi_farkli' };
    return {
        ok: true,
        value: Number(BigInt(revenue.amountMicros)) / Number(BigInt(spend.spendMicros)),
    };
}
/** Birim başına maliyet (CAC / aday başına maliyet) = harcama / adet; aynı koşullar + adet > 0 */
export function costPerOf(spend, count) {
    const g = spendGap(spend);
    if (g !== null || spend === null)
        return { ok: false, eksik: g ?? 'harcama_yok' };
    if (!Number.isFinite(count) || count <= 0)
        return { ok: false, eksik: 'payda_sifir' };
    return { ok: true, value: Number(BigInt(spend.spendMicros)) / 1_000_000 / count };
}
/**
 * Kampanya raporunda bir kampanyanın maliyet ölçüleri (ROAS ve kazanılan müşteri başına maliyet). Kurallar:
 *  - harcama yoksa, eksikse (veri tamlığı), dönemi raporla uyuşmuyorsa (hesap saat dilimi ≠ rapor saat dilimi) ya da birden çok
 *    para birimindeyse İKİSİ DE üretilmez (neden döner);
 *  - ROAS yalnız ciro harcamayla AYNI para birimindeyse (başka para biriminde ciro varsa `para_birimi_farkli`);
 *  - müşteri başına maliyet yalnız kazanılan fırsat > 0 ise.
 * Ciro CRM'de kazanılan fırsat tutarıdır; reklam platformunun dönüşüm sayısı burada KULLANILMAZ.
 */
export function campaignCostMetrics(x) {
    const fail = (eksik) => ({
        roas: { ok: false, eksik },
        cac: { ok: false, eksik },
    });
    if (x.spend.length === 0)
        return fail('harcama_yok');
    if (!x.complete)
        return fail('harcama_eksik');
    if (!x.periodMatches)
        return fail('donem_farkli');
    const currencies = new Set(x.spend.map((s) => s.currency));
    const currency = [...currencies][0];
    if (currencies.size !== 1 || currency === undefined)
        return fail('para_birimi_farkli');
    let micros = 0n;
    for (const s of x.spend)
        micros += microsFromDecimal(s.amount);
    const spend = { currency, spendMicros: micros, complete: true };
    const others = x.revenue.filter((r) => r.currency !== currency && Number(r.amount) !== 0);
    const same = x.revenue.filter((r) => r.currency === currency);
    let roas;
    if (others.length > 0)
        roas = { ok: false, eksik: 'para_birimi_farkli' };
    else if (same.length === 0)
        roas = micros <= 0n ? { ok: false, eksik: 'harcama_sifir' } : { ok: false, eksik: 'gelir_yok' };
    else {
        let rev = 0n;
        try {
            for (const r of same)
                rev += microsFromDecimal(r.amount);
            roas = roasOf({ currency, amountMicros: rev }, spend);
        }
        catch {
            roas = { ok: false, eksik: 'gelir_yok' };
        }
    }
    return { roas, cac: costPerOf(spend, x.won) };
}
// ---------------------------------------------------------------------------------------------------------------------
// Eşitleme durumu ve veri tamlığı
// ---------------------------------------------------------------------------------------------------------------------
/**
 * Eşitlemenin çalışmama nedeni (kalıcı hata DEĞİL; kapı açılınca bir sonraki turda pencere çekilir). Sağlayıcı gönderim
 * kapısının kodlarıyla aynı adlandırma (`provider_blocked`).
 */
export const AD_SYNC_BLOCK_CODES = [
    'connection_pending',
    'connection_revoked',
    'connection_error',
    'connection_disconnected',
    'token_missing',
    'emergency_stop_connection',
    'emergency_stop_tenant',
    'tenant_inactive',
    'adapter_unavailable',
    'platform_hold',
    // v3.3 DEMO: demo çalışma alanının örnek reklam hesabı — hiçbir sağlayıcıya bağlı değildir, eşitleme hiç çalışmaz
    'demo_account',
];
/** Eşitleme hata kodları (kapalı küme; sağlayıcının ham yanıtı saklanmaz) */
export const AD_SYNC_ERROR_CODES = [
    'rate_limited',
    // v3.3: sağlayıcı okuma bütçesi doldu — hata DEĞİL, bekleme (`backoffUntil` yer açılmasının beklendiği andır)
    'provider_budget',
    'transient',
    'invalid_request',
    'invalid_response',
    'platform_config',
    'internal',
];
export const AD_SYNC_STATES = ['never', 'ok', 'stale', 'failing', 'backoff', 'blocked'];
/** Raporda veri tamlığı nedenleri */
export const AD_SPEND_GAP_REASONS = [
    'never_synced',
    'not_synced_to_end',
    'before_coverage',
    'gap',
    'sync_failing',
    'sync_blocked',
];
// ---------------------------------------------------------------------------------------------------------------------
// API sözleşmesi
// ---------------------------------------------------------------------------------------------------------------------
const dateStr = z.string().regex(AD_DATE_RE, 'tarih YYYY-AA-GG biçiminde olmalı');
export const AD_SPEND_GROUPS = ['campaign', 'day'];
export const AD_SPEND_PAGE_MAX = 2000;
/** `GET /ads/spend` sorgusu (tarihler hesap saat dilimindeki takvim günüdür) */
export const adSpendQuerySchema = z
    .object({
    from: dateStr.optional(),
    to: dateStr.optional(),
    accountId: z.guid().optional(),
    /** Kanonik kampanya kimliği (`acquisition_campaigns`) */
    campaignId: z.guid().optional(),
    group: z.enum(AD_SPEND_GROUPS).default('campaign'),
    limit: z.coerce.number().int().min(1).max(AD_SPEND_PAGE_MAX).default(500),
})
    .refine((v) => v.from === undefined || v.to === undefined || v.from <= v.to, {
    message: 'başlangıç bitişten sonra olamaz',
    path: ['from'],
})
    .refine((v) => v.from === undefined ||
    v.to === undefined ||
    daysBetweenIso(v.from, v.to) < AD_SPEND_SYNC.maxRangeDays, { message: `aralık en çok ${String(AD_SPEND_SYNC.maxRangeDays)} gün olabilir`, path: ['to'] });
/** Hesaba bağlı kanonik kampanya (`acquisition_campaigns`, düzey `campaign`) */
export const adCampaignViewSchema = z.object({
    /** Kanonik kampanya kimliği */
    id: z.string(),
    externalId: z.string(),
    name: z.string(),
    /** Castinflow'daki kampanya kaydı durumu (`active` / `archived`) */
    status: z.string(),
});
export const adAccountSyncViewSchema = z.object({
    state: z.enum(AD_SYNC_STATES),
    blockCode: z.enum(AD_SYNC_BLOCK_CODES).nullable(),
    lastSuccessAt: z.string().nullable(),
    lastAttemptAt: z.string().nullable(),
    lastErrorCode: z.string().nullable(),
    consecutiveFailures: z.number().int(),
    syncedThrough: z.string().nullable(),
    coverageFrom: z.string().nullable(),
    nextSyncAt: z.string().nullable(),
    backoffUntil: z.string().nullable(),
});
export const adSpendCompletenessSchema = z.object({
    complete: z.boolean(),
    reasons: z.array(z.enum(AD_SPEND_GAP_REASONS)),
});
export const adAccountViewSchema = z.object({
    /** Reklam hesabı kimliği (= bağlantının dış varlık kaydı) */
    id: z.string(),
    provider: z.enum(['meta_ads_account', 'google_ads_customer', 'simulator']),
    source: z.enum(AD_SPEND_SOURCES),
    /** v3.1 CA-4: simülatör bağlantısı mı (gerçek tür + sahte sağlayıcı; yalnız deneme ortamı) — ekranda etiketlenir */
    simulated: z.boolean().default(false),
    /**
     * v3.3 DEMO: demo çalışma alanının örnek reklam hesabı. Harcama satırları SENTETİKTİR (örnek veri yüklemesiyle yazılır),
     * hiçbir reklam platformundan okunmamıştır; ekranda "Demo verisi" olarak etiketlenir. Eşitleme çalışmaz.
     */
    demo: z.boolean().optional(),
    connectionId: z.string(),
    connectionName: z.string(),
    connectionStatus: z.string(),
    /** Maskeli dış hesap kimliği (son 4 karakter) */
    externalIdMasked: z.string(),
    currency: z.string().nullable(),
    timeZone: z.string().nullable(),
    name: z.string().nullable(),
    sync: adAccountSyncViewSchema,
    campaigns: z.array(adCampaignViewSchema),
});
export const adAccountListSchema = z.object({ accounts: z.array(adAccountViewSchema) });
export const adSpendRowSchema = z.object({
    accountId: z.string(),
    /** Kanonik kampanya kimliği ve platformdaki dış kimliği */
    campaignId: z.string(),
    campaignExternalId: z.string().nullable(),
    campaignName: z.string(),
    /** `group = day` iken gün; `campaign` iken `null` */
    date: z.string().nullable(),
    currency: z.string(),
    /** Harcama, mikro birim (tam sayı dizgesi; para biriminin milyonda biri) */
    spendMicros: z.string(),
    impressions: z.number().int(),
    clicks: z.number().int(),
    /** Platformun saydığı dönüşüm (Castinflow satışı DEĞİL) */
    platformConversions: z.number(),
});
/** Para birimi başına toplam (farklı para birimleri tek sayıda toplanmaz) */
export const adSpendCurrencyTotalSchema = z.object({
    currency: z.string(),
    spendMicros: z.string(),
    impressions: z.number().int(),
    clicks: z.number().int(),
    platformConversions: z.number(),
});
export const adSpendReportSchema = z.object({
    range: z.object({ from: z.string(), to: z.string() }),
    group: z.enum(AD_SPEND_GROUPS),
    accounts: z.array(adAccountViewSchema.omit({ campaigns: true }).extend({
        completeness: adSpendCompletenessSchema,
    })),
    rows: z.array(adSpendRowSchema),
    /** Süzgece uyan satır sayısı (sayfalamadan bağımsız; sayaç = süzgeçli liste toplamı) */
    total: z.number().int(),
    truncated: z.boolean(),
    totals: z.array(adSpendCurrencyTotalSchema),
});
export const AD_SYNC_OUTCOMES = ['synced', 'blocked', 'backoff', 'failed'];
export const adSyncResultSchema = z.object({
    outcome: z.enum(AD_SYNC_OUTCOMES),
    code: z.string().nullable(),
    window: z.object({ from: z.string(), to: z.string() }).nullable(),
    rows: z.number().int(),
});
/**
 * Sosyal medya reklam platformunun `actions` listesinden "platform dönüşümü" sayılan TOPLU eylem türleri. Piksel/çevrim içi
 * alt türleri (ör. `offsite_conversion.fb_pixel_lead`) toplu türün içinde zaten sayıldığından ayrıca eklenmez (çift sayım
 * olmaz). Platform dönüşümü Castinflow satışı değildir.
 */
export const META_CONVERSION_ACTION_TYPES = [
    'lead',
    'purchase',
    'complete_registration',
    'contact',
    'submit_application',
    'schedule',
    'onsite_conversion.messaging_conversation_started_7d',
];
//# sourceMappingURL=ad-spend.js.map